import { useState, useEffect, useCallback } from 'react';
import api, { formatINR } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Store, ChevronDown, ChevronUp, Trash2, Pencil, Upload, FileSpreadsheet } from 'lucide-react';

const emptyVendorForm = { company_name: '', gstin: '', contact: '', address: '', material_type: 'uPVC', series_name: '', color: '', glass_default: '', payment_terms: '', warranty_info: '', gst_rate: 18 };
const emptyProductForm = { design_type: '', width_mm: '', height_mm: '', glass_type: '', hardware_type: '', unit_rate: '', room_label: '' };

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [products, setProducts] = useState([]);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm);
  const [productForm, setProductForm] = useState(emptyProductForm);

  const loadVendors = useCallback(async () => {
    try { const { data } = await api.get('/vendors'); setVendors(data); } catch {}
  }, []);

  useEffect(() => { loadVendors(); }, [loadVendors]);

  const reloadProducts = async (vendorId) => {
    try { const { data } = await api.get(`/vendors/${vendorId}/products`); setProducts(data); } catch {}
  };

  const toggleExpand = async (vendorId) => {
    if (expanded === vendorId) { setExpanded(null); setProducts([]); return; }
    setExpanded(vendorId);
    await reloadProducts(vendorId);
  };

  // --- Vendor handlers ---
  const openAddVendor = () => {
    setEditingVendorId(null);
    setVendorForm(emptyVendorForm);
    setShowVendorDialog(true);
  };

  const openEditVendor = (v, e) => {
    e?.stopPropagation();
    setEditingVendorId(v.id);
    setVendorForm({
      company_name: v.company_name || '', gstin: v.gstin || '', contact: v.contact || '',
      address: v.address || '', material_type: v.material_type || 'uPVC',
      series_name: v.series_name || '', color: v.color || '', glass_default: v.glass_default || '',
      payment_terms: v.payment_terms || '', warranty_info: v.warranty_info || '',
      gst_rate: v.gst_rate ?? 18,
    });
    setShowVendorDialog(true);
  };

  const handleSubmitVendor = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...vendorForm, gst_rate: parseFloat(vendorForm.gst_rate) || 18 };
      if (editingVendorId) {
        await api.put(`/vendors/${editingVendorId}`, payload);
      } else {
        await api.post('/vendors', payload);
      }
      setShowVendorDialog(false);
      setEditingVendorId(null);
      setVendorForm(emptyVendorForm);
      loadVendors();
    } catch {}
  };

  const deleteVendor = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this vendor and all its products?')) return;
    await api.delete(`/vendors/${id}`);
    if (expanded === id) { setExpanded(null); setProducts([]); }
    loadVendors();
  };

  // --- Product handlers ---
  const openAddProduct = () => {
    setEditingProductId(null);
    setProductForm(emptyProductForm);
    setShowProductDialog(true);
  };

  const openEditProduct = (p) => {
    setEditingProductId(p.id);
    setProductForm({
      design_type: p.design_type || '',
      width_mm: p.width_mm ?? '',
      height_mm: p.height_mm ?? '',
      glass_type: p.glass_type || '',
      hardware_type: p.hardware_type || '',
      unit_rate: p.unit_rate ?? '',
      room_label: p.room_label || '',
    });
    setShowProductDialog(true);
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...productForm,
        width_mm: parseFloat(productForm.width_mm) || 0,
        height_mm: parseFloat(productForm.height_mm) || 0,
        unit_rate: parseFloat(productForm.unit_rate) || 0,
      };
      if (editingProductId) {
        await api.put(`/products/${editingProductId}`, payload);
      } else {
        await api.post(`/vendors/${expanded}/products`, payload);
      }
      setShowProductDialog(false);
      setEditingProductId(null);
      setProductForm(emptyProductForm);
      await reloadProducts(expanded);
      loadVendors();
    } catch {}
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/products/${id}`);
    await reloadProducts(expanded);
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !expanded) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/vendors/${expanded}/products/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult({ success: true, message: `Imported ${data.count} products${data.errors?.length ? `. ${data.errors.length} errors.` : ''}` });
      await reloadProducts(expanded);
      loadVendors();
    } catch (err) {
      setImportResult({ success: false, message: err.response?.data?.detail || 'Import failed' });
    }
    setImporting(false);
    e.target.value = '';
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Vendors</h1>
          <p className="text-slate-500 text-sm mt-1">Manage vendor rate cards</p>
        </div>
        <Dialog
          open={showVendorDialog}
          onOpenChange={(o) => { setShowVendorDialog(o); if (!o) { setEditingVendorId(null); setVendorForm(emptyVendorForm); } }}
        >
          <DialogTrigger asChild>
            <Button onClick={openAddVendor} className="bg-[#ed8936] hover:bg-[#dd6b20] text-white" data-testid="add-vendor-btn">
              <Plus size={16} className="mr-1" /> Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">{editingVendorId ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
              <DialogDescription>
                {editingVendorId ? 'Update vendor details.' : 'Enter vendor details to add a new vendor to the system.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitVendor} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Company Name *</Label><Input value={vendorForm.company_name} onChange={e => setVendorForm({...vendorForm, company_name: e.target.value})} required data-testid="vendor-name-input" /></div>
                <div>
                  <Label>Material Type *</Label>
                  <Select value={vendorForm.material_type} onValueChange={v => setVendorForm({...vendorForm, material_type: v})}>
                    <SelectTrigger data-testid="vendor-material-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="uPVC">uPVC</SelectItem><SelectItem value="Aluminium">Aluminium</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>GSTIN</Label><Input value={vendorForm.gstin} onChange={e => setVendorForm({...vendorForm, gstin: e.target.value})} /></div>
                <div><Label>Series/Profile</Label><Input value={vendorForm.series_name} onChange={e => setVendorForm({...vendorForm, series_name: e.target.value})} /></div>
                <div><Label>Color</Label><Input value={vendorForm.color} onChange={e => setVendorForm({...vendorForm, color: e.target.value})} /></div>
                <div className="col-span-2"><Label>Address</Label><Input value={vendorForm.address} onChange={e => setVendorForm({...vendorForm, address: e.target.value})} /></div>
                <div><Label>Glass Default</Label><Input value={vendorForm.glass_default} onChange={e => setVendorForm({...vendorForm, glass_default: e.target.value})} /></div>
                <div><Label>GST Rate (%)</Label><Input type="number" value={vendorForm.gst_rate} onChange={e => setVendorForm({...vendorForm, gst_rate: e.target.value})} /></div>
                <div className="col-span-2"><Label>Payment Terms</Label><Input value={vendorForm.payment_terms} onChange={e => setVendorForm({...vendorForm, payment_terms: e.target.value})} /></div>
                <div className="col-span-2"><Label>Warranty</Label><Input value={vendorForm.warranty_info} onChange={e => setVendorForm({...vendorForm, warranty_info: e.target.value})} /></div>
              </div>
              <Button type="submit" className="w-full bg-[#ed8936] hover:bg-[#dd6b20] text-white" data-testid="save-vendor-btn">
                {editingVendorId ? 'Update Vendor' : 'Save Vendor'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Shared Product Dialog (Add + Edit) */}
      <Dialog
        open={showProductDialog}
        onOpenChange={(o) => { setShowProductDialog(o); if (!o) { setEditingProductId(null); setProductForm(emptyProductForm); } }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{editingProductId ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>
              {editingProductId ? 'Update product details.' : "Add a new product to this vendor's rate card."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitProduct} className="space-y-3">
            <div><Label>Design Type *</Label><Input value={productForm.design_type} onChange={e => setProductForm({...productForm, design_type: e.target.value})} required placeholder="e.g., Casement Window" data-testid="product-design-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Width (mm) *</Label><Input type="number" value={productForm.width_mm} onChange={e => setProductForm({...productForm, width_mm: e.target.value})} required data-testid="product-width-input" /></div>
              <div><Label>Height (mm) *</Label><Input type="number" value={productForm.height_mm} onChange={e => setProductForm({...productForm, height_mm: e.target.value})} required data-testid="product-height-input" /></div>
            </div>
            <div><Label>Glass Type</Label><Input value={productForm.glass_type} onChange={e => setProductForm({...productForm, glass_type: e.target.value})} /></div>
            <div><Label>Hardware</Label><Input value={productForm.hardware_type} onChange={e => setProductForm({...productForm, hardware_type: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Unit Rate (₹) *</Label><Input type="number" value={productForm.unit_rate} onChange={e => setProductForm({...productForm, unit_rate: e.target.value})} required data-testid="product-rate-input" /></div>
              <div><Label>Room Label</Label><Input value={productForm.room_label} onChange={e => setProductForm({...productForm, room_label: e.target.value})} /></div>
            </div>
            <Button type="submit" className="w-full bg-[#ed8936] hover:bg-[#dd6b20] text-white" data-testid="save-product-btn">
              {editingProductId ? 'Update Product' : 'Save Product'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vendor Cards */}
      <div className="space-y-3">
        {vendors.map(v => (
          <Card key={v.id} className="border border-slate-200 hover:border-slate-300 transition-colors" data-testid={`vendor-card-${v.id}`}>
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-5 cursor-pointer" onClick={() => toggleExpand(v.id)}>
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-slate-50">
                    <Store size={20} className="text-[#0B2545]" />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] font-medium text-slate-900">{v.company_name}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="secondary" className={v.material_type === 'Aluminium' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>{v.material_type}</Badge>
                      <span className="text-xs text-slate-400">{v.series_name}</span>
                      <span className="text-xs text-slate-400">{v.product_count || 0} products</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-500 hover:text-[#0B2545] hover:bg-slate-100"
                    onClick={(e) => openEditVendor(v, e)}
                    data-testid={`edit-vendor-${v.id}`}
                    title="Edit vendor"
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => deleteVendor(v.id, e)}
                    data-testid={`delete-vendor-${v.id}`}
                    title="Delete vendor"
                  >
                    <Trash2 size={14} />
                  </Button>
                  {expanded === v.id ? <ChevronUp size={18} className="text-slate-400 ml-1" /> : <ChevronDown size={18} className="text-slate-400 ml-1" />}
                </div>
              </div>

              {expanded === v.id && (
                <div className="border-t border-slate-200 p-5 bg-slate-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-slate-600">Products / Rate Card</p>
                    <div className="flex items-center gap-2">
                      {/* CSV/Excel Import */}
                      <label className="cursor-pointer">
                        <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileImport} className="hidden" data-testid="import-file-input" />
                        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${importing ? 'bg-slate-100 text-slate-400' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 cursor-pointer'}`}>
                          <Upload size={13} /> {importing ? 'Importing...' : 'Import CSV/Excel'}
                        </span>
                      </label>
                      <Button size="sm" onClick={openAddProduct} className="bg-[#0B2545] hover:bg-[#0a1f3a] text-white" data-testid="add-product-btn">
                        <Plus size={14} className="mr-1" /> Add Product
                      </Button>
                    </div>
                  </div>

                  {/* Import result message */}
                  {importResult && (
                    <div className={`mb-3 text-xs px-3 py-2 rounded-lg ${importResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`} data-testid="import-result">
                      {importResult.message}
                    </div>
                  )}

                  {/* CSV format hint */}
                  <div className="mb-3 text-[10px] text-slate-400 flex items-center gap-1">
                    <FileSpreadsheet size={11} /> CSV/Excel columns: <code className="bg-slate-100 px-1 rounded">design_type, width_mm, height_mm, unit_rate</code> (required) + glass_type, hardware_type, room_label (optional)
                  </div>

                  {products.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full text-sm" data-testid="products-table">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Design</th>
                            <th className="text-right px-3 py-2 font-medium text-slate-600">W(mm)</th>
                            <th className="text-right px-3 py-2 font-medium text-slate-600">H(mm)</th>
                            <th className="text-right px-3 py-2 font-medium text-slate-600">Area(Sqm)</th>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Glass</th>
                            <th className="text-right px-3 py-2 font-medium text-slate-600">Rate/Sqm</th>
                            <th className="text-right px-3 py-2 font-medium text-slate-600">Unit Rate</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map(p => (
                            <tr key={p.id} className="border-t border-slate-100 hover:bg-white" data-testid={`product-row-${p.id}`}>
                              <td className="px-3 py-2">{p.design_type}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{p.width_mm}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{p.height_mm}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{p.area_sqm?.toFixed(3)}</td>
                              <td className="px-3 py-2 text-xs">{p.glass_type}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{formatINR(p.rate_per_sqm)}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs font-medium">{formatINR(p.unit_rate)}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => openEditProduct(p)}
                                    className="text-slate-400 hover:text-[#0B2545] p-1 rounded hover:bg-slate-100"
                                    title="Edit product"
                                    data-testid={`edit-product-${p.id}`}
                                  >
                                    <Pencil size={13} />
                                  </button>
                                  <button
                                    onClick={() => deleteProduct(p.id)}
                                    className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                                    title="Delete product"
                                    data-testid={`delete-product-${p.id}`}
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-6">No products yet. Add your first product above.</p>
                  )}

                  {/* Vendor info */}
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div><span className="text-slate-400">Color:</span> <span className="text-slate-700">{v.color || '-'}</span></div>
                    <div><span className="text-slate-400">GSTIN:</span> <span className="text-slate-700">{v.gstin || '-'}</span></div>
                    <div><span className="text-slate-400">Payment:</span> <span className="text-slate-700">{v.payment_terms || '-'}</span></div>
                    <div><span className="text-slate-400">Warranty:</span> <span className="text-slate-700">{v.warranty_info || '-'}</span></div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {vendors.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Store size={48} className="mx-auto mb-3 opacity-30" />
            <p>No vendors yet. Add your first vendor.</p>
          </div>
        )}
      </div>
    </div>
  );
}
